import { PageTitle } from '@statura/ui';
import { Loader2 } from 'lucide-react';
import type { ReactNode } from 'react';

type AppLoadingStateProps = {
  title?: string;
  description?: string;
  variant?: 'full' | 'inline';
  action?: ReactNode;
};

type AuthLoadingCardProps = {
  title?: string;
  description?: string;
};

export function AppLoadingState({
  title = 'Opening Statura',
  description = 'Checking your session and workspace access.',
  variant = 'full',
  action,
}: AppLoadingStateProps) {
  const isInline = variant === 'inline';

  return (
    <div
      className={
        isInline
          ? 'flex min-h-[40vh] items-center justify-center bg-transparent px-4'
          : 'min-h-screen bg-surface-shell px-4 py-6'
      }
      role="status"
      aria-live="polite"
    >
      <div
        className={
          isInline
            ? 'w-full max-w-2xl'
            : 'mx-auto grid min-h-[calc(100vh-3rem)] max-w-6xl gap-4 md:grid-cols-[240px_1fr]'
        }
      >
        {!isInline && (
          <div className="hidden rounded-2xl border border-slate-200 bg-white p-4 shadow-sm md:block">
            <div className="h-8 w-28 rounded-lg bg-slate-200" />
            <div className="mt-8 space-y-3">
              {Array.from({ length: 7 }).map((_, index) => (
                <div
                  key={index}
                  className="h-9 animate-pulse rounded-xl bg-slate-100"
                  style={{ width: `${index % 2 === 0 ? 88 : 72}%` }}
                />
              ))}
            </div>
          </div>
        )}
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm md:p-6">
          <div className="mb-6 flex items-center justify-between border-b border-slate-100 pb-4">
            <div>
              <div className="h-4 w-24 rounded bg-slate-100" />
              <div className="mt-2 h-7 w-56 max-w-full rounded bg-slate-200" />
            </div>
            <Loader2 className="h-8 w-8 animate-spin text-statura-green-accessible" />
          </div>
          <div className="space-y-4">
            <div>
              <PageTitle className="font-heading text-xl text-ink">{title}</PageTitle>
              <p className="mt-2 max-w-xl text-sm text-slate-600">{description}</p>
            </div>
            <div className="grid gap-4 lg:grid-cols-3">
              {Array.from({ length: 3 }).map((_, index) => (
                <div key={index} className="rounded-xl border border-slate-100 p-4">
                  <div className="h-3 w-20 rounded bg-slate-100" />
                  <div className="mt-4 h-8 w-16 rounded bg-slate-200" />
                  <div className="mt-3 h-2 w-full rounded bg-slate-100" />
                </div>
              ))}
            </div>
            <div className="space-y-3 pt-2">
              {Array.from({ length: isInline ? 3 : 5 }).map((_, index) => (
                <div key={index} className="h-11 animate-pulse rounded-xl bg-slate-100" />
              ))}
            </div>
            {action && <div className="pt-2">{action}</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

export function AuthLoadingCard({
  title = 'Loading sign in',
  description = 'Preparing the secure sign-in form.',
}: AuthLoadingCardProps) {
  return (
    <div
      className="w-full max-w-md space-y-8 rounded-2xl bg-white p-8 text-center shadow-card"
      role="status"
      aria-live="polite"
    >
      <div className="space-y-3">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-statura-green/10">
          <Loader2 className="h-6 w-6 animate-spin text-statura-green-accessible" />
        </div>
        <div>
          <PageTitle className="font-heading text-page-title text-text-heading">{title}</PageTitle>
          <p className="mt-2 text-sm text-slate-500">{description}</p>
        </div>
      </div>
    </div>
  );
}
